import { useState } from "react";
import { Visit } from "../types/visit";
import { useScheduleReminder } from "../hooks/useScheduleReminder";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Calendar } from "../../../components/ui/calendar";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { format } from "date-fns";
import { Checkbox } from "../../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "../../../lib/utils";

interface SchedulePhysicianReminderDialogProps {
  open: boolean;
  onClose: () => void;
  currentVisit: Visit;
}

export function SchedulePhysicianReminderDialog({ 
  open, 
  onClose,
  currentVisit 
}: SchedulePhysicianReminderDialogProps) {
  const scheduleReminder = useScheduleReminder();
  const [reminders, setReminders] = useState([{
    id: crypto.randomUUID(),
    date: undefined as Date | undefined,
    notes: "",
    isUrgent: false
  }]);

  const handleSchedule = () => {
    // Validate all reminders have dates
    if (reminders.some(r => !r.date)) return;

    // Schedule all reminders at once
    scheduleReminder.mutate({
      visitId: currentVisit.id,
      reminders: reminders.map(reminder => ({
        id: reminder.id,
        date: reminder.date!.toISOString(),
        notes: reminder.notes,
        isUrgent: reminder.isUrgent
      }))
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Physician Reminder</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {reminders.map((reminder, index) => (
            <div key={reminder.id} className="space-y-6 border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Reminder {index + 1}</h3>
                {reminders.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setReminders(reminders.filter(r => r.id !== reminder.id))}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label>Reminder Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !reminder.date && "text-muted-foreground"
                      )}
                    >
                      {reminder.date ? (
                        format(reminder.date, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={reminder.date}
                      onSelect={(date) => {
                        setReminders(reminders.map(r => 
                          r.id === reminder.id ? { ...r, date } : r
                        ));
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const compareDate = new Date(date);
                        compareDate.setHours(0, 0, 0, 0);
                        return compareDate < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Reminder Notes</Label>
                <Textarea
                  value={reminder.notes}
                  onChange={(e) => {
                    setReminders(reminders.map(r => 
                      r.id === reminder.id ? { ...r, notes: e.target.value } : r
                    ));
                  }}
                  placeholder="Add any specific notes for the reminder"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`urgent-${reminder.id}`}
                  checked={reminder.isUrgent}
                  onCheckedChange={(checked) => {
                    setReminders(reminders.map(r => 
                      r.id === reminder.id ? { ...r, isUrgent: checked as boolean } : r
                    ));
                  }}
                />
                <Label htmlFor={`urgent-${reminder.id}`}>Mark as Urgent</Label>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setReminders([...reminders, {
              id: crypto.randomUUID(),
              date: undefined,
              notes: "",
              isUrgent: false
            }])}
          >
            Add Another Reminder
          </Button>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSchedule} 
            disabled={reminders.some(r => !r.date)}
            className="w-full"
          >
            Schedule Reminder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
